import type { ApolloStateProp } from 'lib/apollo/get-apollo-client'
import type { GetServerSideProps } from 'next'
import type { FC } from 'react'
import type { ContentfulStateProp } from 'services/Contentful/get-contentful-content'
import type { IPageLayoutFields } from 'types/contentful/generated'

import { getConfig } from '@shared/config'
import { minWidthFromTheme } from '@sok/design-system'
import { getServerSideApolloClient } from 'lib/apollo/get-apollo-client'
import { getSelectedBrandFromApollo } from 'lib/apollo/get-selected-brand-from-apollo'
import { NextSeo } from 'next-seo'
import {
  getContentfulContent,
  getPageLayoutContent,
} from 'services/Contentful/get-contentful-content'
import styled from 'styled-components'
import { CONTENT_PAGE_MAX_WIDTH } from 'styles/layout'
import { nofollow, noindex } from 'utils/content-pages/robots'
import { useCanonicalUrl } from 'utils/hooks/use-canonical-url'

import { ContentPageLayout } from './components/ContentPageLayout'
import { PageLayoutSections } from './components/PageLayoutSections'

const { contentful } = getConfig()

const StyledPageContent = styled.div`
  background-color: ${({ theme }) => theme.color.white};
  width: 100%;

  ${({ theme }) => minWidthFromTheme(theme).desktop} {
    padding: 1rem;
    .product-grid__carousel,
    .featured-products__content-page {
      .arrow-left {
        left: 4vw;
      }

      .arrow-right {
        right: 4vw;
      }
    }

    .horizontal-content-grid {
      justify-content: center;
      margin: 0 auto;
    }
  }
`

interface ContentPageProps {
  page: IPageLayoutFields | null
}

export const ContentPage: FC<ContentPageProps> = ({ page }) => {
  const seoCanonical = useCanonicalUrl()

  const { hideMainNavigation, robotsMetaTags, sections, seoDescription, seoTitle, showFooter } =
    page ?? {}

  return (
    <>
      <NextSeo
        title={seoTitle}
        description={seoDescription}
        canonical={seoCanonical}
        nofollow={nofollow(robotsMetaTags)}
        noindex={noindex(robotsMetaTags)}
      />
      <ContentPageLayout hideGlobalNavigation={hideMainNavigation} showFooter={showFooter}>
        <StyledPageContent>
          <ContentWrapper>
            <PageLayoutSections sections={sections} />
          </ContentWrapper>
        </StyledPageContent>
      </ContentPageLayout>
    </>
  )
}

const ContentWrapper = styled.div({
  maxWidth: CONTENT_PAGE_MAX_WIDTH,
  margin: 'auto',
})

export const getServerSideProps: GetServerSideProps<
  ApolloStateProp & ContentfulStateProp & ContentPageProps
> = async (context) => {
  const apolloClient = await getServerSideApolloClient(context)

  const brand = getSelectedBrandFromApollo(apolloClient)

  /** Join possible multiple params back into a single path like `foo/bar` */
  const pageParam =
    context.params &&
    (Array.isArray(context.params?.page) ? context.params.page.join('/') : context.params.page)

  const [contentfulState, pageContent] = await Promise.all([
    getContentfulContent(brand),
    getPageLayoutContent({ path: String(pageParam) }),
  ])

  const isFrontPage = pageContent?.pageType === 'front-page'

  if (
    !pageContent ||
    /* frontpages are not visible as content pages unless live preview is enabled */
    (isFrontPage && !contentful.livePreview)
  ) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      apolloState: apolloClient.cache.extract(),
      contentfulState,
      page: pageContent,
    },
  }
}
